import { useCallback, useEffect, useState } from 'react'

import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Paper, TextField } from '@material-ui/core'
import { DataGridPro, GridSortModel } from '@mui/x-data-grid-pro'
import { includes } from 'lodash'

import api from 'common/api'
import permissions from 'common/system-permissions'
import useUser from 'common/use-user'
import { useTableHeightListener } from 'routes/highschools/components/TableComponents/useTableHeightListener'
import { User } from 'store/user'

import DeleteUserDialog from './DeleteUserDialog'
import InviteUsersModal from './InviteUsersModal'
import ManageRolesModal from './ManageRolesModal'
import ManageUserModal from './ManageUserModal'
import getColumns from './columns'

const DEFAULT_RESULTS = { Total: 0, Users: [] as User[] }
const DEFAULT_SORT: GridSortModel = [{ field: 'LastName', sort: 'asc' }]

export const ManageUsers = () => {
  const user = useUser()
  const { Scid: scid, Permissions: userPermissions } = user || { Scid: '', Permissions: [] }

  const [loaded, setLoaded] = useState(false)
  const [results, setResults] = useState(DEFAULT_RESULTS)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT)
  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [openEdit, setOpenEdit] = useState(false)
  const [isReviewingNewUser, setIsReviewingNewUser] = useState(false)
  const [editingUser, setEditingUser] = useState<User | undefined>(undefined)
  const [openDelete, setOpenDelete] = useState(false)
  const [deleteUser, setDeleteUser] = useState<User | undefined>(undefined)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [openInvite, setOpenInvite] = useState(false)
  const [openRoles, setOpenRoles] = useState(false)
  const [editingRolesUser, setEditingRolesUser] = useState<Partial<User>>({})

  const [tableHeight, tableRef] = useTableHeightListener()
  const isUserPermitted = includes(userPermissions, permissions.MANAGE_ADMISSIONS)

  const fetchUsers = useCallback(() => {
    const sortField = sortModel[0]?.field ?? 'LastName'
    const sortDir = sortModel[0]?.sort ?? 'asc'
    const sort = sortDir === 'asc' ? sortField : `-${sortField}`
    const from = page * pageSize

    api
      .get(`/api/college/${scid}/admissions_officers?pageSize=${pageSize}&from=${from}&sort=${sort}&search=${search}`)
      .then((raw) => {
        const data = raw as { Total: number; Users: User[] }
        const currentUserIdx = data.Users.findIndex((u: User) => u.Id === user?.Id)
        if (currentUserIdx > -1) {
          data.Users[currentUserIdx].Roles = user?.Roles
        }
        setLoaded(true)
        setResults({ Total: data.Total, Users: data.Users || [] })
      })
      .catch(() => {
        setLoaded(true)
        setResults(DEFAULT_RESULTS)
      })
  }, [scid, user, page, pageSize, sortModel, search])

  useEffect(() => {
    // debounce search input by 500ms
    const timer = setTimeout(() => {
      setSearch(inputValue)
      setPage(0)
    }, 500)
    return () => clearTimeout(timer)
  }, [inputValue])

  useEffect(() => {
    document.title = 'Scoir - Manage Users'
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const saveUser = (userRecord: User, opts: { inviteNow: boolean; reviewNewUser: boolean }) => {
    let prom
    if (opts.reviewNewUser) {
      setOpenEdit(false)
      setEditingUser(undefined)
      setOpenRoles(true)
      setEditingRolesUser(userRecord)
      return
    } else if (userRecord.Id) {
      prom = api.put(`/api/college/${scid}/admissions_officers/${userRecord.Id}`, userRecord)
    } else {
      prom = api
        .post(`/api/users?invite=${opts.inviteNow}&type=AdmissionsOfficer`, {
          ...userRecord,
          Scid: scid,
        })
        .then((raw) => {
          const newUser = raw as User
          setOpenRoles(true)
          setEditingRolesUser(newUser)
        })
    }
    return prom.then(() => {
      setOpenEdit(false)
      setEditingUser(undefined)
      fetchUsers()
    })
  }

  const inviteStaff = (userIds: string[]) => {
    api.post('/api/invites', userIds).then(() => fetchUsers())
  }

  const onDeleteUser = () => {
    api.delete(`/api/college/${scid}/admissions_officers/${deleteUser!.Id}`).then(() => {
      setOpenDelete(false)
      fetchUsers()
    })
  }

  const handleDenyNewUser = () => {
    setDeleteUser(editingRolesUser as User)
    setOpenRoles(false)
    setEditingRolesUser({})
    setOpenDelete(true)
  }

  const handleApproveNewUser = () => {
    const userId = editingRolesUser.Id!
    saveUser({ ...editingRolesUser, NeedsApproval: false, Active: true } as User, {
      inviteNow: false,
      reviewNewUser: false,
    })?.then(() => inviteStaff([userId]))
    setOpenRoles(false)
    setEditingRolesUser({})
  }

  const closeRolesModal = (hasChanges: boolean) => {
    setOpenRoles(false)
    if (hasChanges) fetchUsers()
  }

  const columns = getColumns({
    isUserPermitted,
    onEdit: (record, isReviewingNewUser) => {
      setOpenEdit(true)
      setIsReviewingNewUser(isReviewingNewUser)
      setEditingUser(record)
    },
    onDelete: (record) => {
      setOpenDelete(true)
      setDeleteUser(record)
    },
    onRolesEdit: (record) => {
      setOpenRoles(true)
      setEditingRolesUser(record)
    },
  })

  return (
    <Paper
      elevation={0}
      component={Box}
      // @ts-expect-error Box props not being recognized on Paper component
      margin={3}
      padding={3}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <TextField
          placeholder="Search..."
          size="small"
          variant="outlined"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <Box>
          {isUserPermitted && selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenInvite(true)}
              style={{ marginRight: 8 }}
            >
              Invite Staff
            </Button>
          )}
          {isUserPermitted && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setOpenEdit(true)
                setEditingUser(undefined)
                setIsReviewingNewUser(false)
              }}
              startIcon={<FontAwesomeIcon icon={faPlus} />}
            >
              Add New User
            </Button>
          )}
        </Box>
      </Box>

      <DataGridPro
        ref={tableRef}
        // 48px for box horizontal margin
        sx={{ '&.MuiDataGrid-root': { height: `calc(100vh - ${(tableHeight || 0) + 48}px)` } }}
        disableColumnReorder
        disableColumnFilter
        disableColumnMenu
        paginationMode="server"
        sortingMode="server"
        rows={results.Users}
        getRowId={(row) => row.Id}
        rowCount={results.Total}
        columns={columns}
        loading={!loaded}
        checkboxSelection
        selectionModel={selectedIds}
        onSelectionModelChange={(ids) => setSelectedIds(ids as string[])}
        pagination
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize)
          setPage(0)
        }}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
      />

      <ManageUserModal
        open={openEdit}
        user={editingUser}
        isNew={editingUser === undefined}
        isReviewingNewUser={isReviewingNewUser}
        onClose={() => {
          setOpenEdit(false)
          setIsReviewingNewUser(false)
        }}
        onSubmitChanges={(userRecord: User, opts: { inviteNow: boolean; reviewNewUser: boolean }) =>
          saveUser(userRecord, opts)
        }
      />
      <DeleteUserDialog
        open={openDelete}
        user={deleteUser}
        onClose={() => setOpenDelete(false)}
        onConfirm={() => onDeleteUser()}
      />
      <InviteUsersModal
        open={openInvite}
        onClose={() => setOpenInvite(false)}
        users={results.Users.filter((u) => selectedIds.includes(u.Id))}
        onSubmit={(userIds: string[]) => inviteStaff(userIds)}
      />
      <ManageRolesModal
        isUserPermitted={isUserPermitted && user?.Id !== editingRolesUser.Id}
        open={openRoles}
        onClose={(hasChanges: boolean) => closeRolesModal(hasChanges)}
        userTarget={editingRolesUser}
        isReviewingNewUser={isReviewingNewUser}
        onDeleteUser={handleDenyNewUser}
        onInviteUser={handleApproveNewUser}
      />
    </Paper>
  )
}

export default ManageUsers
